/**
 * Connection detail page
 * Tabbed view: Schema Browser, Scan History, Settings
 */

import { useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Zap,
  ScanSearch,
  Trash2,
  AlertCircle,
  Database,
  Loader2,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ConnectionStatusBadge, ScanStatusBadge } from '@/components/discovery'
import { SchemaTree } from '@/components/discovery/schema-tree'
import {
  useConnection,
  useTestConnection,
  useDeleteConnection,
  useScans,
  useStartScan,
  useColumns,
} from '@/hooks/use-discovery'
import { dbTypeLabels, formatTimestamp } from '@/lib/discovery-utils'

export function ConnectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: connection, isLoading, isError, error } = useConnection(id)
  const testConnectionMutation = useTestConnection()
  const deleteConnectionMutation = useDeleteConnection()
  const startScanMutation = useStartScan()

  const { data: scansData, isLoading: scansLoading } = useScans(
    id ? { connectionId: id, pageSize: 20 } : {}
  )
  const scans = scansData?.data ?? []

  // Schema browser state
  const [selectedSchema, setSelectedSchema] = useState<string | undefined>()
  const [selectedTable, setSelectedTable] = useState<string | undefined>()

  const { data: columns, isLoading: columnsLoading } = useColumns(
    id,
    selectedSchema,
    selectedTable
  )

  const handleSelectTable = useCallback(
    (schema: string, table: string) => {
      setSelectedSchema(schema)
      setSelectedTable(table)
    },
    []
  )

  const handleTest = useCallback(() => {
    if (id) {
      testConnectionMutation.mutate(id)
    }
  }, [id, testConnectionMutation])

  const handleScan = useCallback(() => {
    if (id) {
      startScanMutation.mutate({ connectionId: id })
    }
  }, [id, startScanMutation])

  const handleDelete = useCallback(() => {
    if (id) {
      deleteConnectionMutation.mutate(id)
    }
  }, [id, deleteConnectionMutation])

  if (isLoading) {
    return (
      <>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Skeleton className="h-5 w-48" />
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    )
  }

  if (isError || !connection) {
    return (
      <>
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() => navigate('/discovery/connections')}
          >
            <ArrowLeft className="mr-2 h-3.5 w-3.5" />
            Back
          </Button>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : 'Connection not found'}
            </AlertDescription>
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={() => navigate('/discovery/connections')}
          >
            <ArrowLeft className="mr-2 h-3.5 w-3.5" />
            Back
          </Button>
          <Database className="h-4 w-4 text-muted-foreground" />
          <h1 className="text-base font-medium">{connection.name}</h1>
          <ConnectionStatusBadge status={connection.status} />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handleTest}
            disabled={testConnectionMutation.isPending}
          >
            {testConnectionMutation.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Zap className="mr-2 h-3.5 w-3.5" />
            )}
            Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={handleScan}
            disabled={startScanMutation.isPending}
          >
            {startScanMutation.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <ScanSearch className="mr-2 h-3.5 w-3.5" />
            )}
            Scan
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Connection Info */}
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <p className="font-medium">Type</p>
            <p className="text-muted-foreground">
              {dbTypeLabels[connection.databaseType] ?? connection.databaseType}
            </p>
          </div>
          <div>
            <p className="font-medium">Host</p>
            <p className="text-muted-foreground">
              {connection.host}:{connection.port}
            </p>
          </div>
          <div>
            <p className="font-medium">Database</p>
            <p className="text-muted-foreground">{connection.databaseName}</p>
          </div>
          <div>
            <p className="font-medium">Last Tested</p>
            <p className="text-muted-foreground">
              {formatTimestamp(connection.lastTestedAt)}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="schema">
          <TabsList>
            <TabsTrigger value="schema">Schema Browser</TabsTrigger>
            <TabsTrigger value="scans">Scan History</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Schema Browser */}
          <TabsContent value="schema" className="mt-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Tree */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Schemas &amp; Tables</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto p-2">
                  <SchemaTree
                    connectionId={connection.id}
                    onSelectTable={handleSelectTable}
                    selectedSchema={selectedSchema}
                    selectedTable={selectedTable}
                  />
                </CardContent>
              </Card>

              {/* Column Detail */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {selectedTable
                      ? `${selectedSchema}.${selectedTable}`
                      : 'Select a table'}
                  </CardTitle>
                  <CardDescription>
                    {selectedTable
                      ? 'Column details for the selected table'
                      : 'Click a table in the tree to view its columns'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!selectedTable ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      Select a table from the schema tree to view column details.
                    </p>
                  ) : columnsLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Column</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Nullable</TableHead>
                          <TableHead>Default</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {columns?.map((col) => (
                          <TableRow key={col.columnName}>
                            <TableCell className="font-mono text-sm">
                              {col.columnName}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {col.dataType}
                            </TableCell>
                            <TableCell>{col.isNullable ? 'Yes' : 'No'}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {col.columnDefault ?? '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!columns || columns.length === 0) && (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              className="text-center text-muted-foreground"
                            >
                              No columns found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Scan History */}
          <TabsContent value="scans" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Scan History</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleScan}
                    disabled={startScanMutation.isPending}
                  >
                    {startScanMutation.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ScanSearch className="mr-2 h-3.5 w-3.5" />
                    )}
                    Start Scan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {scansLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : scans.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    No scans yet. Start a scan to classify data in this database.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Columns</TableHead>
                        <TableHead className="text-right">Flagged</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Completed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scans.map((scan) => (
                        <TableRow
                          key={scan.id}
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(`/discovery/scans/${scan.id}/results`)
                          }
                        >
                          <TableCell>
                            <ScanStatusBadge status={scan.status} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {scan.columnsClassified}/{scan.columnsScanned}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {0}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTimestamp(scan.startedAt)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatTimestamp(scan.completedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Danger Zone</CardTitle>
                <CardDescription>
                  Permanently delete this connection and all associated scan data.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete Connection
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Connection</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete &quot;{connection.name}&quot;
                        and all associated scan results. This action cannot be
                        undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
